import { useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useActivityFeed } from "@/hooks/useActivityFeed";
import { ActivityCard } from "./ActivityCard";
import { ActivityFilterTabs } from "./ActivityFilterTabs";
import { ActivityFeedSkeleton } from "./ActivityFeedSkeleton";
import type { ActivityFilterType, Activity, ActivityActionType } from "@/types/activity";
import { cn } from "@/lib/utils";

/**
 * Mock activities for demo/empty state
 */
const MOCK_ACTIVITIES: Activity[] = [
  {
    id: "mock-1",
    actor_id: "mock-actor-1",
    actor_name: "Johan Andersson",
    action_type: "file.uploaded" as ActivityActionType,
    org_id: "mock-org",
    project_id: "mock-project-1",
    project_name: "Nya Karolinska",
    file_id: "mock-file-1",
    file_name: "Bygghandlingar_v2.pdf",
    report_id: null,
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
  },
  {
    id: "mock-2",
    actor_id: "mock-actor-2",
    actor_name: "Sara Lindqvist",
    action_type: "report.generated" as ActivityActionType,
    org_id: "mock-org",
    project_id: "mock-project-2",
    project_name: "Slussen Projekt",
    file_id: null,
    report_id: "mock-report-1",
    report_name: "Riskanalys Q1",
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), // 5 hours ago
  },
  {
    id: "mock-3",
    actor_id: "mock-actor-3",
    actor_name: "Erik Johansson",
    action_type: "file.uploaded" as ActivityActionType,
    org_id: "mock-org",
    project_id: "mock-project-3",
    project_name: "Hagastaden",
    file_id: "mock-file-2",
    file_name: "Ritningar_El.dwg",
    report_id: null,
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
  },
  {
    id: "mock-4",
    actor_id: "mock-actor-1",
    actor_name: "Johan Andersson",
    action_type: "project.created" as ActivityActionType,
    org_id: "mock-org",
    project_id: "mock-project-1",
    project_name: "Nya Karolinska",
    file_id: null,
    report_id: null,
    created_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(), // 2 days ago
  },
];

interface ActivityFeedProps {
  scope: "org" | "project";
  scopeId: string;
  showProjectName?: boolean; // Show project name on cards (for org feed)
  showHeader?: boolean;
}

export function ActivityFeed({
  scope,
  scopeId,
  showProjectName = false,
  showHeader = true,
}: ActivityFeedProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ActivityFilterType>("all");
  const [cursor, setCursor] = useState<string | null>(null);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [allActivities, setAllActivities] = useState<Activity[]>([]);

  const { data, isLoading, refetch, isFetching } = useActivityFeed(scope, scopeId, {
    limit: 6,
    type: activeTab,
    cursor: cursor ?? undefined,
  });

  // Handle tab change - reset cursor and accumulated activities
  const handleTabChange = (newTab: ActivityFilterType) => {
    setActiveTab(newTab);
    setCursor(null);
    setAllActivities([]);
  };

  // Handle manual refresh
  const handleRefresh = async () => {
    setIsManualRefresh(true);
    setCursor(null);
    setAllActivities([]);
    await refetch();
    setIsManualRefresh(false);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (data?.next_cursor) {
      // Accumulate activities before loading more
      setAllActivities((prev) => [...prev, ...(data?.activities || [])]);
      setCursor(data.next_cursor);
    }
  };

  // Combine accumulated activities with current page
  const realActivities = cursor
    ? [...allActivities, ...(data?.activities || [])]
    : data?.activities || [];

  // Use mock data when no real activities exist
  const currentActivities = realActivities.length > 0 ? realActivities : MOCK_ACTIVITIES;
  const isShowingMockData = realActivities.length === 0 && !isLoading && !isManualRefresh;

  // Show skeleton on initial load or manual refresh
  const showSkeleton = isLoading || isManualRefresh;

  return (
    <div className="flex flex-col space-y-4">
      {/* Header with title and refresh button */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <h2 className="text-ui-title-3">{t("activityFeed.title")}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isFetching}
            className="text-muted-foreground"
          >
            <RefreshCw className={cn("size-4", isFetching && "animate-spin")} />
            <span className="sr-only">{t("activityFeed.refresh")}</span>
          </Button>
        </div>
      )}

      {/* Filter tabs */}
      <ActivityFilterTabs
        value={activeTab}
        onChange={handleTabChange}
        counts={data?.counts || { all: 0, files: 0, reports: 0 }}
      />

      {/* Content */}
      <div className="scrollbar flex-1 overflow-y-auto">
        {showSkeleton ? (
          <ActivityFeedSkeleton />
        ) : (
          <div className="flex flex-col">
            {isShowingMockData && (
              <p className="text-ui-body-xs text-muted-foreground mb-4 italic px-2">
                {t("activityFeed.sampleData")}
              </p>
            )}
            <div className="flex flex-col divide-y divide-border">
              {currentActivities.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={showProjectName ? activity : { ...activity, project_name: undefined }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Load more button */}
      {data?.has_more && !showSkeleton && (
        <div className="mt-4">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            {isFetching ? t("common.loading") : t("activityFeed.loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
}
